import React, { useMemo, useState } from "react";
import { countRowPermissions } from "./permissionMatrixConfig";
import PermissionMatrixTable, { AccessMatrixLegend } from "./PermissionMatrixTable";

function groupScreensByModule(rows) {
  const map = new Map();
  for (const row of rows || []) {
    const key = Number(row.module_id) || row.module_name || "other";
    if (!map.has(key)) {
      map.set(key, {
        moduleId: row.module_id,
        moduleName: row.module_name || "Other",
        moduleSlug: row.module_slug || "",
        rows: [],
      });
    }
    map.get(key).rows.push(row);
  }
  return [...map.values()].sort((a, b) =>
    String(a.moduleName).localeCompare(String(b.moduleName)),
  );
}

function Chevron({ open }) {
  return (
    <svg
      className={`ub-screen-module__chevron${open ? " ub-screen-module__chevron--open" : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Screen permissions grouped by parent module — same card style as Menu modules.
 */
export default function PermissionSubmodulePanels({
  rows,
  nameColumn,
  onToggle,
  onPatchRow,
  onPatchColumn,
  disabled,
  isRowDisabled,
}) {
  const groups = useMemo(() => groupScreensByModule(rows), [rows]);
  const [openModules, setOpenModules] = useState(() => new Set());

  const allModuleKeys = useMemo(
    () => groups.map((g) => String(g.moduleId ?? g.moduleName)),
    [groups],
  );

  const isOpen = (key) =>
    openModules.size === 0 ? true : openModules.has(key);

  const toggleModule = (key) => {
    setOpenModules((prev) => {
      const next = new Set(prev.size === 0 ? allModuleKeys : prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!groups.length) {
    return (
      <div className="ub-screen-perms__empty">
        <p className="ub-screen-perms__empty-title">No screens to configure</p>
        <p className="ub-screen-perms__empty-text">
          Enable one or more modules under <strong>Menu modules</strong> first (Main
          screens are always listed here), then assign screen-level access.
        </p>
      </div>
    );
  }

  return (
    <div className="ub-screen-perms">
      <AccessMatrixLegend
        rows={rows}
        nameColumn={nameColumn}
        disabled={disabled}
        isRowDisabled={isRowDisabled}
        onPatchColumn={onPatchColumn}
        onToggle={onToggle}
      />

      <div className="ub-screen-perms__modules">
        {groups.map((group) => {
          const key = String(group.moduleId ?? group.moduleName);
          const open = isOpen(key);
          const activeScreens = group.rows.filter(
            (r) => countRowPermissions(r) > 0,
          ).length;

          return (
            <section
              key={key}
              className={`ub-screen-module${open ? " ub-screen-module--open" : ""}`}
            >
              <button
                type="button"
                className="ub-screen-module__head"
                aria-expanded={open}
                onClick={() => toggleModule(key)}
              >
                <span className="ub-screen-module__accent" aria-hidden />
                <span className="ub-screen-module__info">
                  <span className="ub-screen-module__name">{group.moduleName}</span>
                  {group.moduleSlug ? (
                    <span className="ub-screen-module__slug">{group.moduleSlug}</span>
                  ) : null}
                </span>
                <span className="ub-screen-module__stat">
                  {activeScreens}/{group.rows.length} active
                </span>
                <Chevron open={open} />
              </button>

              {open ? (
                <div className="ub-screen-module__body">
                  <PermissionMatrixTable
                    rows={group.rows}
                    nameColumn={nameColumn}
                    onToggle={onToggle}
                    onPatchRow={onPatchRow}
                    onPatchColumn={onPatchColumn}
                    disabled={disabled}
                    isRowDisabled={isRowDisabled}
                    showLegend={false}
                  />
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
